import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import type { Me, Notification, NotificationList } from '@split/shared';
import { useState } from 'react';
import { api, isApiError } from './http';
import { mk, qk } from './keys';
import { noteSignedInUser, type SettlementActionVars } from './queryClient';

const PAGE = 30;

/** Current user; `null` when signed out (401). Cached so the app boots offline. */
export function useSession() {
  return useQuery({
    queryKey: qk.session,
    queryFn: async (): Promise<Me | null> => {
      try {
        return (await api('session')).user;
      } catch (e) {
        if (isApiError(e, 'UNAUTHENTICATED') || isApiError(e, 'ACCOUNT_DISABLED')) return null;
        throw e;
      }
    },
    staleTime: 5 * 60_000,
  });
}

/**
 * The signed-in user. Only call below `<RequireAuth>`. Right after signing out, a screen may
 * render once more before `<RequireAuth>` takes it away: it keeps the last user it had.
 */
export function useMe(): Me {
  const { data } = useSession();
  const [last, setLast] = useState(data);
  if (data && data !== last) setLast(data);
  const me = data ?? last;
  if (!me) throw new Error('useMe() used without a session');
  return me;
}

/** Sets the signed-in user (sign in, invite, setup, profile edits); see `noteSignedInUser`. */
export function setSessionUser(client: QueryClient, user: Me | null) {
  if (user) noteSignedInUser(client, user.id);
  client.setQueryData(qk.session, user);
}

export function useOverview() {
  return useQuery({ queryKey: qk.overview, queryFn: () => api('overview') });
}

export function useGroups() {
  return useQuery({ queryKey: qk.groups, queryFn: async () => (await api('listGroups')).items });
}

export function useGroup(id: string | undefined) {
  return useQuery({
    queryKey: qk.group(id ?? ''),
    queryFn: () => api('getGroup', { params: { id: id ?? '' } }),
    enabled: id !== undefined && id !== '',
  });
}

export function useGroupFeed(id: string) {
  return useInfiniteQuery({
    queryKey: qk.groupFeed(id),
    queryFn: ({ pageParam }) =>
      api('groupFeed', { params: { id }, query: { cursor: pageParam ?? undefined, limit: PAGE } }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
}

export function useGroupBalances(id: string) {
  return useQuery({
    queryKey: qk.groupBalances(id),
    queryFn: () => api('groupBalances', { params: { id } }),
  });
}

export function usePeople() {
  return useQuery({ queryKey: qk.people, queryFn: async () => (await api('people')).items });
}

export function usePerson(userId: string) {
  return useQuery({
    queryKey: qk.person(userId),
    queryFn: () => api('person', { params: { userId } }),
  });
}

export function useUserProfile(id: string | null | undefined) {
  return useQuery({
    queryKey: qk.userProfile(id ?? ''),
    queryFn: () => api('userProfile', { params: { id: id ?? '' } }),
    enabled: !!id,
  });
}

export function useExpense(id: string) {
  return useQuery({
    queryKey: qk.expense(id),
    queryFn: () => api('getExpense', { params: { id } }),
  });
}

export function useActivity(groupId: string | null) {
  return useInfiniteQuery({
    queryKey: qk.activity(groupId),
    queryFn: ({ pageParam }) =>
      groupId
        ? api('groupActivity', {
            params: { id: groupId },
            query: { cursor: pageParam ?? undefined, limit: PAGE },
          })
        : api('activity', { query: { cursor: pageParam ?? undefined, limit: PAGE } }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
}

export function useNotifications() {
  return useInfiniteQuery({
    queryKey: qk.notifications,
    queryFn: ({ pageParam }) =>
      api('notifications', { query: { cursor: pageParam ?? undefined, limit: PAGE } }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
}

/** Unresolved actionable rows, for the Home "Needs your attention" card. */
export function useAttention() {
  return useQuery({
    queryKey: qk.attention,
    queryFn: async () =>
      (await api('notifications', { query: { filter: 'actionable', limit: 5 } })).items,
  });
}

export function useUnreadCount() {
  return useQuery({
    queryKey: qk.unreadCount,
    queryFn: async () => (await api('unreadCount')).unreadCount,
    refetchInterval: 60_000,
  });
}

export function useNotificationPrefs() {
  return useQuery({
    queryKey: qk.notificationPrefs,
    queryFn: () => api('notificationPreferences'),
  });
}

export function useFxRates(date: string | null = null) {
  return useQuery({
    queryKey: qk.fx(date),
    queryFn: () => api('fxRates', { query: { date: date ?? undefined } }),
    staleTime: 6 * 60 * 60_000,
  });
}

// ---------------------------------------------------------------------------
// Inbox mutations (optimistic, offline-queued). Shared by the inbox, Home and push actions.
// ---------------------------------------------------------------------------

type InboxPages = { pages: NotificationList[]; pageParams: unknown[] };

function patchNotification(
  client: QueryClient,
  id: string | null | undefined,
  patch: (n: Notification) => Notification,
) {
  client.setQueryData<InboxPages>(qk.notifications, (old) =>
    old
      ? {
          ...old,
          pages: old.pages.map((p) => ({
            ...p,
            items: p.items.map((n) => (n.id === id ? patch(n) : n)),
          })),
        }
      : old,
  );
  client.setQueryData<Notification[]>(qk.attention, (old) =>
    old?.map((n) => (n.id === id ? patch(n) : n)),
  );
}

function decrementUnread(client: QueryClient, wasUnread: boolean) {
  if (!wasUnread) return;
  client.setQueryData<number>(qk.unreadCount, (n) => (n === undefined ? n : Math.max(0, n - 1)));
}

function findNotification(client: QueryClient, id: string | null | undefined) {
  if (!id) return undefined;
  const pages = client.getQueryData<InboxPages>(qk.notifications)?.pages ?? [];
  for (const p of pages) for (const n of p.items) if (n.id === id) return n;
  return client.getQueryData<Notification[]>(qk.attention)?.find((n) => n.id === id);
}

export function useConfirmSettlement() {
  const client = useQueryClient();
  return useMutation<unknown, Error, SettlementActionVars>({
    mutationKey: mk.confirmSettlement,
    onMutate: (vars) => {
      const n = findNotification(client, vars.notificationId);
      patchNotification(client, vars.notificationId, (x) => ({
        ...x,
        actionable: false,
        resolution: 'confirmed',
        read: true,
      }));
      decrementUnread(client, n ? !n.read && !n.muted : false);
    },
  });
}

export function useRejectSettlement() {
  const client = useQueryClient();
  return useMutation<unknown, Error, SettlementActionVars>({
    mutationKey: mk.rejectSettlement,
    onMutate: (vars) => {
      const n = findNotification(client, vars.notificationId);
      patchNotification(client, vars.notificationId, (x) => ({
        ...x,
        actionable: false,
        resolution: 'rejected',
        read: true,
      }));
      decrementUnread(client, n ? !n.read && !n.muted : false);
    },
  });
}

/** Optimistically flips a "Not received" row (shown while the Undo toast is up). */
export function markRejectedLocally(
  client: QueryClient,
  notificationId: string | null | undefined,
) {
  const n = findNotification(client, notificationId);
  const before = n ? { ...n } : undefined;
  patchNotification(client, notificationId, (x) => ({
    ...x,
    actionable: false,
    resolution: 'rejected',
    read: true,
  }));
  decrementUnread(client, n ? !n.read && !n.muted : false);
  return () => {
    if (!before) return;
    patchNotification(client, notificationId, () => before);
    if (!before.read && !before.muted) {
      client.setQueryData<number>(qk.unreadCount, (c) => (c === undefined ? c : c + 1));
    }
  };
}

export function useMarkRead() {
  const client = useQueryClient();
  return useMutation<unknown, Error, { id: string }>({
    mutationKey: mk.markRead,
    onMutate: ({ id }) => {
      const n = findNotification(client, id);
      if (!n || n.read) return;
      patchNotification(client, id, (x) => ({ ...x, read: true }));
      decrementUnread(client, !n.muted);
    },
  });
}

export function useMarkAllRead() {
  const client = useQueryClient();
  return useMutation({
    mutationKey: mk.markAllRead,
    onMutate: () => {
      client.setQueryData<InboxPages>(qk.notifications, (old) =>
        old
          ? {
              ...old,
              pages: old.pages.map((p) => ({
                ...p,
                unreadCount: 0,
                items: p.items.map((n) => ({ ...n, read: true })),
              })),
            }
          : old,
      );
      client.setQueryData<number>(qk.unreadCount, 0);
    },
  });
}
